import React, { useState, useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'
import DrawingCanvas from './DrawingCanvas'
import toast from 'react-hot-toast'

/**
 * DrawingModal Component
 * 
 * Modal component for drawing canvas functionality.
 * Similar structure to AI modal but designed for drawing interface.
 */
const DrawingModal = ({ 
  isOpen, 
  onClose, 
  onDone, 
  backgroundImage = null,
  mode = 'create',
  noteTitle = ''
}) => {
  const canvasRef = useRef(null)
  const [canvasDataUrl, setCanvasDataUrl] = useState('')
  const [currentColor, setCurrentColor] = useState('#000000')
  const [brushSize, setBrushSize] = useState(3)
  const [isProcessing, setIsProcessing] = useState(false)
  const [currentTool, setCurrentTool] = useState('pen')
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)
  
  // Color presets
  const colorPresets = [
    '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF',
    '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#800080',
    '#808080', '#FFC0CB', '#A52A2A', '#008000'
  ]
  
  // Brush sizes
  const brushSizes = [1, 3, 5, 10, 15, 20]
  
  /**
   * Handle canvas drawing changes
   */
  const handleDrawingChange = useCallback((dataUrl) => {
    setCanvasDataUrl(dataUrl)
    
    // Update undo/redo button states
    if (canvasRef.current) {
      setCanUndo(canvasRef.current.canUndo || false)
      setCanRedo(canvasRef.current.canRedo || false)
    }
  }, [])
  
  /**
   * Handle color selection
   */
  const handleColorSelect = useCallback((color) => {
    setCurrentColor(color)
    // Canvas will pick up the color change through props
  }, [])
  
  /**
   * Handle brush size change
   */
  const handleBrushSizeChange = useCallback((size) => {
    setBrushSize(size)
    // Canvas will pick up the size change through props
  }, [])

  /**
   * Handle tool selection
   */
  const handleToolSelect = useCallback((tool) => {
    setCurrentTool(tool)
    // Canvas will pick up the tool change through props
  }, [])

  /**
   * Handle undo
   */
  const handleUndo = useCallback(() => {
    if (canvasRef.current && canvasRef.current.undo) {
      canvasRef.current.undo()
    }
  }, [])

  /**
   * Handle redo
   */
  const handleRedo = useCallback(() => {
    if (canvasRef.current && canvasRef.current.redo) {
      canvasRef.current.redo()
    }
  }, [])

  
  /**
   * Clear canvas
   */
  const handleClear = useCallback(() => {
    if (canvasRef.current && canvasRef.current.clearCanvas) {
      canvasRef.current.clearCanvas()
    }
  }, [])
  
  /**
   * Reset canvas (edit mode only)
   */
  const handleReset = useCallback(() => {
    if (canvasRef.current && canvasRef.current.resetCanvas && mode === 'edit') {
      canvasRef.current.resetCanvas()
    }
  }, [mode])
  
  /**
   * Handle done button click
   */
  const handleDone = useCallback(async () => {
    if (!canvasDataUrl) {
      toast.error('Please draw something before saving')
      return
    }
    
    setIsProcessing(true)
    
    try {
      // Get the cropped canvas data URL
      const croppedDataUrl = canvasRef.current?.getCanvasDataUrl()
      
      if (!croppedDataUrl) {
        throw new Error('No drawing data available')
      }
      
      // Call parent's done handler with cropped canvas data
      await onDone(croppedDataUrl)
      
    } catch (error) {
      console.error('Failed to process drawing:', error)
      toast.error('Failed to save drawing')
    } finally {
      setIsProcessing(false)
    }
  }, [canvasDataUrl, onDone])
  
  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    if (isProcessing) return
    onClose()
  }, [isProcessing, onClose])
  
  if (!isOpen) return null
  
  return ReactDOM.createPortal(
    <>
      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 16px;
          width: 16px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        .slider::-moz-range-thumb {
          height: 16px;
          width: 16px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        .canvas-container::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .canvas-container::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 4px;
        }
        .canvas-container::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.6);
          border-radius: 4px;
        }
        .canvas-container::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.8);
        }
        .canvas-container {
          -webkit-overflow-scrolling: touch;
        }
        /* AMOLED mode color swatch fix - force colors to display */
        div[style*="--color"] {
          background: var(--color) !important;
          background-color: var(--color) !important;
          background-image: none !important;
        }
        /* Force color display in dark mode */
        .dark div[style*="--color"] {
          background: var(--color) !important;
          background-color: var(--color) !important;
          background-image: none !important;
        }
      `}</style>
      <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-[60] p-2 sm:p-4">
      <div className="bg-white/40 dark:bg-gray-800/40 backdrop-blur-[40px] rounded-2xl shadow-2xl border border-white/30 dark:border-gray-700/30 max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col mx-2 sm:mx-4" style={{ backdropFilter: 'blur(40px)' }}>
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200/30 dark:border-gray-700/30 bg-gradient-to-r from-gray-50/20 to-gray-100/20 dark:from-gray-800/20 dark:to-gray-900/20 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {mode === 'edit' ? 'Edit Image with Drawing' : 'Drawing Canvas'}
          </h3>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
            title="Close"
            disabled={isProcessing}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Drawing Toolbar */}
        <div className="px-4 sm:px-6 py-3 border-b border-gray-200/30 dark:border-gray-700/30 bg-gradient-to-r from-gray-50/20 to-gray-100/20 dark:from-gray-800/20 dark:to-gray-900/20 flex-shrink-0">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Drawing Tools */}
            <div className="flex items-center gap-2">
              <span className="hidden sm:block text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Tools</span>
              <div className="flex gap-1 bg-gray-100/5 dark:bg-gray-700/5 rounded-xl p-1">
                {/* Pen Tool */}
                <button
                  type="button"
                  className={`p-2.5 rounded-lg transition-all duration-200 ${
                    currentTool === 'pen'
                      ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-gray-200 hover:shadow-md'
                  }`}
                  onClick={() => handleToolSelect('pen')}
                  title="Pen"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>

                {/* Eraser Tool */}
                <button
                  type="button"
                  className={`p-2.5 rounded-lg transition-all duration-200 ${
                    currentTool === 'eraser'
                      ? 'bg-red-500 text-white shadow-lg shadow-red-500/25'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-gray-200 hover:shadow-md'
                  }`}
                  onClick={() => handleToolSelect('eraser')}
                  title="Eraser"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path d="M20.25 9.84C20.25 9.38 20.07 8.94 19.74 8.61L15.39 4.26C14.71 3.58 13.61 3.58 12.93 4.26L4.26 12.93C3.93 13.26 3.75 13.7 3.75 14.16C3.75 14.62 3.93 15.06 4.26 15.39L8.61 19.74C8.95 20.08 9.39 20.25 9.84 20.25C9.88 20.25 9.93 20.25 9.97 20.24C9.99 20.24 10 20.24 10.02 20.24H18.01C18.42 20.24 18.76 19.9 18.76 19.49C18.76 19.08 18.42 18.74 18.01 18.74H12.06L19.73 11.07C20.06 10.74 20.24 10.3 20.24 9.84H20.25ZM5.32 14.33C5.26 14.27 5.25 14.2 5.25 14.16C5.25 14.12 5.25 14.05 5.32 13.99L9.12 10.19L13.81 14.88L10.01 18.68C9.92 18.77 9.77 18.77 9.67 18.68L5.32 14.32V14.33ZM18.68 10.02L14.88 13.82L10.19 9.13L13.99 5.33C14.08 5.24 14.23 5.24 14.33 5.33L18.68 9.69C18.74 9.75 18.75 9.82 18.75 9.86C18.75 9.9 18.75 9.97 18.68 10.03V10.02Z" fill="currentColor"/>
                  </svg>
                </button>

                {/* Rectangle Tool */}
                <button
                  type="button"
                  className={`p-2.5 rounded-lg transition-all duration-200 ${
                    currentTool === 'rectangle'
                      ? 'bg-green-500 text-white shadow-lg shadow-green-500/25'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-gray-200 hover:shadow-md'
                  }`}
                  onClick={() => handleToolSelect('rectangle')}
                  title="Rectangle"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth={2.5} />
                  </svg>
                </button>

                {/* Circle Tool */}
                <button
                  type="button"
                  className={`p-2.5 rounded-lg transition-all duration-200 ${
                    currentTool === 'circle'
                      ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/25'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-gray-200 hover:shadow-md'
                  }`}
                  onClick={() => handleToolSelect('circle')}
                  title="Circle"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" strokeWidth={2.5} />
                  </svg>
                </button>

                {/* Line Tool */}
                <button
                  type="button"
                  className={`p-2.5 rounded-lg transition-all duration-200 ${
                    currentTool === 'line'
                      ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-gray-200 hover:shadow-md'
                  }`}
                  onClick={() => handleToolSelect('line')}
                  title="Line"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 20L20 4" />
                  </svg>
                </button>

                {/* Fill Tool */}
                <button
                  type="button"
                  className={`p-2.5 rounded-lg transition-all duration-200 ${
                    currentTool === 'fill'
                      ? 'bg-pink-500 text-white shadow-lg shadow-pink-500/25'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-gray-200 hover:shadow-md'
                  }`}
                  onClick={() => handleToolSelect('fill')}
                  title="Fill"
                >
                  <svg className="w-5 h-5" viewBox="0 0 32 32">
                    <g fill="none" fillRule="evenodd">
                      <path d="m0 0h32v32h-32z"/>
                      <path d="m22.8134874 9.36880578c4.4910294 4.49102932 6.6764277 9.84225252 4.7238062 11.79487402-.1787611.1787611-.3860068.3228404-.6184694.4336437l-14.5761222 9.8281526-11.30760366-11.3076036 9.72126696-14.82064481c.1052268-.19413453.2349175-.36967331.3898602-.52461599 1.9526215-1.95262145 7.1762326.10516473 11.6672619 4.59619408zm5.1872476 12.83119422 2.6132495 3.9198743c.9621719 1.4432578.5721747 3.3932439-.8710831 4.3554158-.5159208.3439472-1.1221068.5274855-1.7421664.5274855-1.73458 0-3.140735-1.4061549-3.140735-3.140735 0-.6200596.1835384-1.2262456.5274855-1.7421663zm0 3.6057756-.9491489 1.4234991c-.099939.1499085-.1625907.3207994-.1836538.4984668l-.0079323.1342991c0 .6300106.5107245 1.140735 1.140735 1.140735.2252096 0 .4453803-.0666623.632766-.1915861.5242005-.349467.6658499-1.0577144.316383-1.5819148zm-17.2311689-16.87817569-7.16348832 10.93236179 8.97601352 8.9760135 10.8625671-7.3115451c-2.3250236-.7279494-5.1522872-2.5346049-7.7022388-5.0845565-2.50805-2.50805-4.2572322-5.2445739-4.9728535-7.51227369zm1.790873-2.74077464c-.2550497.2550497-.2683428 1.21983152.3283312 2.73446538.7494529 1.90245745 2.2660792 4.10258565 4.2678629 6.10436935 3.8126715 3.8126716 8.1356078 5.5546453 8.9664468 4.7238062.8308391-.8308391-.9111346-5.1537753-4.7238062-8.9664469-2.0017836-2.00178364-4.2019119-3.51840989-6.1043693-4.26786283-1.5146339-.59667394-2.4794157-.58338091-2.7344654-.3283312z" fill="currentColor" fillRule="nonzero"/>
                    </g>
                  </svg>
                </button>

                {/* Text Tool */}
                <button
                  type="button"
                  className={`p-2.5 rounded-lg transition-all duration-200 ${
                    currentTool === 'text'
                      ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/25'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-gray-200 hover:shadow-md'
                  }`}
                  onClick={() => handleToolSelect('text')}
                  title="Text"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M12 3V21M9 21H15M19 6V3H5V6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {/* Pan Tool */}
                <button
                  type="button"
                  className={`p-2.5 rounded-lg transition-all duration-200 ${
                    currentTool === 'pan'
                      ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/25'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-gray-200 hover:shadow-md'
                  }`}
                  onClick={() => handleToolSelect('pan')}
                  title="Pan"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fillRule="evenodd" clipRule="evenodd" d="M11.2071 3.6797C11.0909 3.85386 11 4.14834 11 4.5V6V11C11 11.5523 10.5523 12 10 12C9.44772 12 9 11.5523 9 11V6C9 5.64834 8.90906 5.35386 8.79295 5.1797C8.6966 5.03518 8.61209 5 8.5 5C8.38791 5 8.3034 5.03518 8.20705 5.1797C8.09094 5.35386 8 5.64834 8 6V12V15C8 15.5523 7.55228 16 7 16C6.44772 16 6 15.5523 6 15V12C6 11.6483 5.90906 11.3539 5.79295 11.1797C5.6966 11.0352 5.61209 11 5.5 11C5.38791 11 5.3034 11.0352 5.20705 11.1797C5.09094 11.3539 5 11.6483 5 12V16C5 17.033 5.70057 18.1402 7.0547 19.0429C8.3875 19.9315 10.1939 20.5 12 20.5C15.6675 20.5 18 18.251 18 16V9C18 8.64834 17.9091 8.35386 17.7929 8.1797C17.6966 8.03518 17.6121 8 17.5 8C17.3879 8 17.3034 8.03518 17.2071 8.1797C17.0909 8.35386 17 8.64834 17 9V12C17 12.5523 16.5523 13 16 13C15.4477 13 15 12.5523 15 12V9V6C15 5.64834 14.9091 5.35386 14.7929 5.1797C14.6966 5.03518 14.6121 5 14.5 5C14.3879 5 14.3034 5.03518 14.2071 5.1797C14.0909 5.35386 14 5.64834 14 6V11C14 11.5523 13.5523 12 13 12C12.4477 12 12 11.5523 12 11V6V4.5C12 4.14834 11.9091 3.85386 11.7929 3.6797C11.6966 3.53518 11.6121 3.5 11.5 3.5C11.3879 3.5 11.3034 3.53518 11.2071 3.6797ZM13.7452 3.12242C13.975 3.04395 14.227 3 14.5 3C15.3879 3 16.0534 3.46482 16.4571 4.0703C16.8409 4.64614 17 5.35166 17 6V6.05195C17.1578 6.01815 17.3245 6 17.5 6C18.3879 6 19.0534 6.46482 19.4571 7.0703C19.8409 7.64614 20 8.35166 20 9V16C20 19.749 16.3325 22.5 12 22.5C9.80613 22.5 7.6125 21.8185 5.9453 20.7071C4.29943 19.6098 3 17.967 3 16V12C3 11.3517 3.15906 10.6461 3.54295 10.0703C3.9466 9.46482 4.61209 9 5.5 9C5.67545 9 5.84222 9.01815 6 9.05195V6C6 5.35166 6.15906 4.64614 6.54295 4.0703C6.9466 3.46482 7.61209 3 8.5 3C8.77302 3 9.02501 3.04395 9.25482 3.12242C9.33156 2.92998 9.427 2.74423 9.54295 2.5703C9.9466 1.96482 10.6121 1.5 11.5 1.5C12.3879 1.5 13.0534 1.96482 13.4571 2.5703C13.573 2.74423 13.6684 2.92998 13.7452 3.12242Z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Undo/Redo Buttons */}
            <div className="flex items-center gap-1 bg-gray-100/5 dark:bg-gray-700/5 rounded-xl p-1">
              <button
                type="button"
                onClick={handleUndo}
                disabled={!canUndo}
                className={`p-2.5 rounded-lg transition-all duration-200 ${
                  canUndo
                    ? 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-gray-200 hover:shadow-md'
                    : 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                }`}
                title="Undo"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
              </button>
              
              <button
                type="button"
                onClick={handleRedo}
                disabled={!canRedo}
                className={`p-2.5 rounded-lg transition-all duration-200 ${
                  canRedo
                    ? 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-gray-200 hover:shadow-md'
                    : 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                }`}
                title="Redo"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 10H11a8 8 0 00-8 8v2m18-10l-6 6m6-6l-6-6" />
                </svg>
              </button>
              
            </div>

            {/* Color Presets */}
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="hidden sm:block text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Color</span>
              <div className="flex gap-1 bg-gray-100/5 dark:bg-gray-700/5 rounded-xl p-1">
                {colorPresets.map((color) => (
                  <div
                    key={color}
                    className={`w-7 h-7 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:scale-110 ${
                      currentColor === color 
                        ? 'border-gray-800 dark:border-gray-200 shadow-lg shadow-gray-500/25' 
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-500 dark:hover:border-gray-400 hover:shadow-md'
                    }`}
                    style={{ 
                      backgroundColor: color,
                      background: color,
                      backgroundImage: 'none',
                      // Force color display
                      '--color': color
                    }}
                    onClick={() => handleColorSelect(color)}
                    title={color}
                  />
                ))}
              </div>
            </div>

            {/* Brush Size */}
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="hidden sm:block text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Size</span>
              <div className="flex gap-1 bg-gray-100/5 dark:bg-gray-700/5 rounded-xl p-1">
                {brushSizes.map((size) => (
                  <button
                    key={size}
                    type="button"
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 ${
                      brushSize === size
                        ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-gray-900 dark:hover:text-gray-200 hover:shadow-md'
                    }`}
                    onClick={() => handleBrushSizeChange(size)}
                  >
                    {size}px
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Canvas Area */}
        <div className={`canvas-container flex-1 min-h-0 bg-gradient-to-br from-gray-50/40 to-gray-100/40 dark:from-gray-900/40 dark:to-gray-800/40 backdrop-blur-[40px] overflow-auto ${
          currentTool === 'pan' ? 'cursor-grab' : ''
        }`} style={{ 
          backdropFilter: 'blur(40px)',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent'
        }}>
          <div className="relative w-full h-full">
            {currentTool === 'pan' && (
              <div className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium z-10">
                Pan Mode - Drag to scroll canvas
              </div>
            )}
            <div className="w-full h-full flex items-start justify-start p-1">
          <DrawingCanvas
            ref={canvasRef}
            onDrawingChange={handleDrawingChange}
            backgroundImage={backgroundImage}
            mode={mode}
            currentColor={currentColor}
            brushSize={brushSize}
                currentTool={currentTool}
          />
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-4 sm:px-6 py-4 border-t border-gray-200/30 dark:border-gray-700/30 bg-gradient-to-r from-gray-50/20 to-gray-100/20 dark:from-gray-800/20 dark:to-gray-900/20 flex-shrink-0">
          {/* Mobile: All buttons in one line, Desktop: Split layout */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            {/* Mobile: Single row with all buttons */}
            <div className="flex gap-2 w-full sm:hidden">
              <button
                type="button"
                onClick={handleClear}
                className="px-3 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 hover:shadow-md transition-all duration-200 flex-1"
                disabled={isProcessing}
              >
                Clear
              </button>
              {mode === 'edit' && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-3 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 hover:shadow-md transition-all duration-200 flex-1"
                  disabled={isProcessing}
                >
                  Reset
                </button>
              )}
              <button
                type="button"
                onClick={handleClose}
                className="px-3 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 hover:shadow-md transition-all duration-200 flex-1"
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDone}
                disabled={!canvasDataUrl || isProcessing}
                className="px-3 py-2 text-xs font-semibold text-white bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center space-x-2 flex-1"
              >
                {isProcessing ? (
                  <>
                    <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Done</span>
                )}
              </button>
            </div>

            {/* Desktop: Split layout */}
            <div className="hidden sm:flex items-center gap-3">
              <button
                type="button"
                onClick={handleClear}
                className="px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 hover:shadow-md transition-all duration-200"
                disabled={isProcessing}
              >
                Clear Canvas
              </button>
              {mode === 'edit' && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 hover:shadow-md transition-all duration-200"
                  disabled={isProcessing}
                >
                  Reset to Original
                </button>
              )}
            </div>
            
            <div className="hidden sm:flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 hover:shadow-md transition-all duration-200"
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDone}
                disabled={!canvasDataUrl || isProcessing}
                className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2"
              >
                {isProcessing ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Cropping & Saving...</span>
                  </>
                ) : (
                  <span>Done</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>,
    document.body
  )
}

export default DrawingModal
